import { useState, useRef, useEffect } from 'react';
import { Platform, TextInput } from 'react-native';

import {
  getHash,
  removeListener,
  startOtpListener,
  useOtpVerify,
} from 'react-native-otp-verify';

interface UseOTPVerificationParams {
  noOfDigits: number;
  resendTimerCount: number;
  handleOtpInput: (otp: string[]) => void;
  triggerResendOTP: () => void;
  blockResend: boolean;
  showErrorBorder : boolean
  cursorPosition : number
}

export const useOTPVerification = ({
  noOfDigits,
  resendTimerCount,
  handleOtpInput,
  triggerResendOTP,
  blockResend,
  showErrorBorder,
  cursorPosition
}: UseOTPVerificationParams) => {
  const [otp, setOtp] = useState<string[]>(Array(noOfDigits).fill(''));
  const [timer, setTimer] = useState<number>(resendTimerCount);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showHint, setShowHint] = useState<boolean>(false);
  const [otpResent, setOtpResent] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  const inputRefs = useRef<(TextInput | null)[]>(Array(noOfDigits).fill(null));


  useEffect(() => {
    if (Platform.OS === 'android') {
      getHash().then((_) => {
      }).catch(console.log);
      startOtpListener(message => {
        // extract the otp using regex e.g. the below regex extracts 4 digit otp from message
        const otpMatch = message.match(/\b\d{6}\b/);

        // Extract the OTP or handle if not found
        const otp = otpMatch ? otpMatch[0] : null;

        if (otp) {
          setTimeout(() => {
            setOtp(otp.split(''))
          }, 100)
        } else {
        }
      });
      return () => removeListener();
    }
  }, []);

  useEffect(() => {
    if (showErrorBorder) {
      //To avoid blocking the main thread
      const timeout = setTimeout(resetOtp, 10); // Add delay
      return () => clearTimeout(timeout);
    }
  }, [showErrorBorder]);

  

  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      setShowInfo(false);
      return;
    }

    const interval = setInterval(() => {
      setTimer((prevTimer) => prevTimer - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    setTimeout(() => {
      //preventing a race condition here
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, Platform.select({
      ios: 0,
      android: 200,
      web: 200
    }))
  }, []);

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleOtpChange = (text: string, index: number): void => {
    setOtpResent(false);
    if (!/^[0-9]*$/.test(text) || text === '') return;

    const newOtp = [...otp];
    let currentIndex = index;

    const isAlreadyFilled = newOtp[currentIndex] !== '';
    if (isAlreadyFilled) {
      newOtp[currentIndex] = text[cursorPosition];
      setOtp(newOtp);
      inputRefs.current[newOtp.join('').length]?.focus();
      return;
    }

    for (const char of text) {
      if (currentIndex >= noOfDigits) break;
      newOtp[currentIndex] = char;
      currentIndex++;
    }
    setOtp(newOtp);

    if (currentIndex < noOfDigits) {
      inputRefs.current[currentIndex]?.focus();
    }
  };

  const handleBackspace = (index: number, event: any): void => {
    if(blockResend){
      return
    }
    let inputIndex = otp.findLastIndex((el) => el !== '');
    const isEmpty = otp[index] === '';
    const isPrevEmpty = otp[index-1] === '';

    inputIndex = inputIndex === -1 ? index : inputIndex;
      if (event.nativeEvent.key === 'Backspace') {
          const newOtp = [...otp];
          newOtp[inputIndex] = '';
            setOtp(newOtp);
          if (isEmpty) {
              if (isPrevEmpty) {
                  inputIndex > 0 && inputRefs.current[inputIndex]?.focus();

              } else {
                  index > 0 && inputRefs.current[index - 1]?.focus();
              }
          } else {
              inputIndex > 0 && inputRefs.current[inputIndex]?.focus();
          }
      }
  };

  const resetOtp = (): void => {
    if(blockResend){
      return
    }
    setOtp(Array(noOfDigits).fill(''));
    for (let i = 0; i < inputRefs.current.length; i++) {
      inputRefs.current[i]?.clear();
    }
    inputRefs.current[0]?.focus();
  };

  const handleResend = (): void => {
    if (blockResend) {
      return;
    }
    if (!canResend) {
      setShowInfo(true);
      return;
    }
    setCanResend(false);
    setShowHint(true);
    setOtpResent(true);
    triggerResendOTP();
    setTimer(resendTimerCount);
    resetOtp();
  };

  useEffect(() => {
    handleOtpInput(otp);
  }, [otp]);

  return {
    otp,
    timer,
    canResend,
    showInfo,
    showHint,
    otpResent,
    focusedIndex,
    inputRefs,
    handleFocus,
    handleOtpChange,
    handleBackspace,
    handleResend,
    resetOtp,
  };
};
